/**
 * PaceToWinCard — the "Win the Day" recovery guidance tile that lives
 * inside Store Pulse (Manager Dashboard → Step 2).
 *
 * Why it exists (user ask 2026-02): the app was heavy on historical data
 * entry but light on decision support. Managers needed a single glance
 * that answered "how much more do we need to sell, transact, and capture
 * to win the day?" without navigating away from the Store Pulse section.
 *
 * Data source: the `pace` block on GET /api/tally/{date}. Zero new
 * fetches — ManagerDashboard already polls that endpoint for Store
 * Pulse. All math (plan_remaining, tx_needed, captures_needed,
 * required_pace_per_hour) is computed server-side in
 * `_compute_trends_and_pace()` so this component is purely
 * presentational.
 *
 * States (drives copy + gradient):
 *   winning        → all targets hit
 *   needs_push     → within 15% of hitting Plan
 *   critical       → gap > 40% of Plan
 *   gate_no_plan   → manager hasn't entered Plan yet (show CTA)
 *   gate_no_comp   → Plan entered but Comp is blank (per user 2026-02:
 *                    "make the manager enter it") — surface an inline
 *                    "Set Comp Goal" nag chip.
 *
 * Placement: ManagerDashboard.js renders this between
 * `<EmailCaptureGlanceCard>` and `<div className="dashboard-card">…
 * <GoalCalculator/></div>` (see Store Pulse block in ManagerDashboard).
 */
import { Target, Rocket, ShoppingCart, EnvelopeSimple, ClockCountdown, Lightning, Trophy } from "@phosphor-icons/react";

const usd = (n) => {
  if (!Number.isFinite(n) || n === 0) return "$0";
  const abs = Math.abs(n);
  if (abs >= 1000) return `$${(n / 1000).toFixed(abs >= 10000 ? 1 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

function pickState(pace) {
  if (!pace) return { key: "gate_no_plan" };
  if (!pace.plan_set) return { key: "gate_no_plan" };
  const remaining = pace.plan_remaining || 0;
  const plan = pace.plan_sales || 1;
  const gapPct = remaining / plan; // 0 → hit, 1 → nothing done yet
  if (pace.at_plan) return { key: "winning" };
  if (gapPct <= 0.15) return { key: "needs_push" };
  if (gapPct >= 0.40) return { key: "critical" };
  return { key: "in_progress" };
}

const STATE_STYLE = {
  winning: {
    gradient: "linear-gradient(135deg, #10b981 0%, #14b8a6 55%, #06b6d4 100%)",
    Icon: Trophy,
    kicker: "Winning",
    tagline: "Goal locked. Keep the energy.",
  },
  needs_push: {
    gradient: "linear-gradient(135deg, #0066FF 0%, #4f46e5 50%, #7c3aed 100%)",
    Icon: Lightning,
    kicker: "One More Push",
    tagline: "Close — one strong shift hits Plan.",
  },
  in_progress: {
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #d946ef 55%, #ec4899 100%)",
    Icon: Rocket,
    kicker: "Game On",
    tagline: "Right on the recovery lane.",
  },
  critical: {
    gradient: "linear-gradient(135deg, #f43f5e 0%, #ec4899 55%, #d946ef 100%)",
    Icon: Rocket,
    kicker: "Critical Focus",
    tagline: "Big gap — every dollar and email counts.",
  },
};

// Small row helper — keeps the render body scannable.
const PaceLine = ({ Icon, tint, label, value, testid }) => (
  <div
    className="flex items-center gap-2.5 rounded-2xl bg-white/12 backdrop-blur-[2px] ring-1 ring-white/20 px-3 py-2"
    data-testid={testid}
  >
    <span
      className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-xl text-white shadow-sm"
      style={{ background: tint }}
    >
      <Icon weight="fill" className="w-4 h-4" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] opacity-80 leading-none">
        {label}
      </p>
      <p className="text-base sm:text-lg font-black leading-tight mt-0.5 truncate" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
        {value}
      </p>
    </div>
  </div>
);

export default function PaceToWinCard({ tallyData, onEditBizDay }) {
  const pace = tallyData?.pace;
  const state = pickState(pace);

  // ---------- Gate: Plan not yet entered ----------
  // Sister stores that skip AM entry land here. Surface a friendly
  // prompt instead of blank numbers.
  if (state.key === "gate_no_plan") {
    return (
      <section
        data-testid="pace-to-win-card"
        data-state="gate_no_plan"
        className="relative overflow-hidden rounded-3xl shadow-md ring-1 ring-slate-200 bg-white p-5"
      >
        <div className="flex items-start gap-3">
          <span
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-2xl text-white shadow-md"
            style={{ background: "linear-gradient(135deg, #8b5cf6, #ec4899)" }}
          >
            <Target weight="fill" className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-fuchsia-500 leading-none">
              Pace to Win
            </p>
            <h3 className="text-lg font-black text-slate-900 leading-tight mt-1" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              Enter today&apos;s Sales Plan to unlock recovery guidance
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Plan $, Comp Goal $, and ADS live on Eyes on the Biz →
              Zone Sheet.
            </p>
            {onEditBizDay && (
              <button
                type="button"
                onClick={onEditBizDay}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-black uppercase tracking-wider hover:bg-slate-800 active:scale-95 transition-all"
                data-testid="pace-to-win-set-plan"
              >
                Enter Plan &amp; Comp →
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }

  const style = STATE_STYLE[state.key] || STATE_STYLE.in_progress;
  const Icon = style.Icon;
  const compMissing = !pace.comp_goal_set;
  const adsMissing = !pace.ads_set;

  return (
    <section
      data-testid="pace-to-win-card"
      data-state={state.key}
      className="relative overflow-hidden rounded-3xl shadow-xl text-white ring-1 ring-white/20"
      style={{ background: style.gradient }}
    >
      {/* Confetti dot pattern — matches WinTheDayHero's visual language. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.16] pointer-events-none mix-blend-screen"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.85) 1.4px, transparent 1.5px)",
          backgroundSize: "18px 18px",
        }}
      />
      <div className="relative px-5 sm:px-6 py-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] sm:text-[11px] font-black tracking-[0.22em] uppercase opacity-85">
              Pace to Win
            </p>
            <h2
              className="text-2xl sm:text-3xl font-black leading-[0.95] mt-1 drop-shadow-[0_1px_2px_rgba(0,0,0,0.15)]"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
              data-testid="pace-to-win-title"
            >
              {style.kicker}
            </h2>
            <p className="text-xs sm:text-sm font-semibold opacity-90 mt-1">
              {style.tagline}
            </p>
          </div>
          <span
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30 shadow-md"
          >
            <Icon weight="fill" className="w-5 h-5" />
          </span>
        </div>

        {/* Nag chip — Comp Goal missing (user ask 2026-02: "make the
            manager enter it"). Shown inline so it can't be missed but
            doesn't block the rest of the numbers from rendering. */}
        {compMissing && onEditBizDay && (
          <button
            type="button"
            onClick={onEditBizDay}
            className="mt-3 w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-2xl bg-white/95 text-slate-900 text-xs font-black uppercase tracking-wider shadow-md hover:bg-white active:scale-[0.98] transition-all"
            data-testid="pace-to-win-set-comp"
          >
            <span className="truncate">🎯 Comp Goal not set — Tap to enter</span>
            <span aria-hidden>→</span>
          </button>
        )}

        {/* Recovery grid — Plan / Comp / Tx / Emails. Comp only shown
            when the goal is set (per the "make the manager enter it"
            rule — no default projection). */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <PaceLine
            Icon={Target}
            tint="linear-gradient(135deg, #f59e0b, #f97316)"
            label="Need to hit Plan"
            value={pace.plan_remaining > 0 ? usd(pace.plan_remaining) : "Hit!"}
            testid="pace-plan-remaining"
          />
          {pace.comp_goal_set ? (
            <PaceLine
              Icon={Rocket}
              tint="linear-gradient(135deg, #10b981, #06b6d4)"
              label="Need to beat Comp"
              value={pace.comp_remaining > 0 ? usd(pace.comp_remaining) : "Beat!"}
              testid="pace-comp-remaining"
            />
          ) : (
            <div
              className="flex items-center gap-2.5 rounded-2xl bg-white/8 ring-1 ring-white/15 px-3 py-2 opacity-70"
              data-testid="pace-comp-remaining-empty"
            >
              <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-xl bg-white/20 text-white">
                <Rocket weight="fill" className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] leading-none opacity-90">
                  Comp Goal
                </p>
                <p className="text-xs font-bold leading-tight mt-0.5">
                  Set it to unlock
                </p>
              </div>
            </div>
          )}
          <PaceLine
            Icon={ShoppingCart}
            tint="linear-gradient(135deg, #6366f1, #8b5cf6)"
            label={adsMissing ? "Transactions needed" : "≈ Transactions needed"}
            value={
              adsMissing
                ? "—"
                : pace.tx_needed_for_plan > 0
                  ? `${pace.tx_needed_for_plan.toLocaleString()} more`
                  : "Covered"
            }
            testid="pace-tx-needed"
          />
          <PaceLine
            Icon={EnvelopeSimple}
            tint="linear-gradient(135deg, #ec4899, #f43f5e)"
            label={`Captures for ${Math.round((tallyData?.goal_rate || 0.35) * 100)}% Goal`}
            value={
              pace.captures_needed_for_goal > 0
                ? `${pace.captures_needed_for_goal} more`
                : "Hit!"
            }
            testid="pace-captures-needed"
          />
        </div>

        {/* Pace-per-hour line — close-to-day is primary, next-checkpoint
            is a subtle sub-line so managers can pick either lens. */}
        {(pace.required_pace_per_hour > 0 || pace.required_pace_next_cp > 0) && (
          <div
            className="mt-4 flex items-start gap-2.5 rounded-2xl bg-white/15 backdrop-blur-sm ring-1 ring-white/25 px-3.5 py-3"
            data-testid="pace-per-hour"
          >
            <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/25 shadow-inner">
              <ClockCountdown weight="fill" className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] opacity-85 leading-none">
                Required pace
              </p>
              {pace.required_pace_per_hour > 0 ? (
                <p className="text-lg font-black leading-tight mt-0.5" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                  {usd(pace.required_pace_per_hour)}/hr
                  <span className="text-xs font-bold opacity-75 ml-1.5 whitespace-nowrap">
                    for {pace.hours_to_close.toFixed(1)}h to close
                  </span>
                </p>
              ) : (
                <p className="text-lg font-black leading-tight mt-0.5">
                  Store closed — hit Plan or move on.
                </p>
              )}
              {pace.required_pace_next_cp > 0 && pace.next_checkpoint && (
                <p className="text-[11px] font-semibold opacity-80 mt-0.5">
                  → {usd(pace.required_pace_next_cp)}/hr to hit {pace.next_checkpoint.toUpperCase()} checkpoint
                  ({pace.hours_to_next_checkpoint.toFixed(1)}h)
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
